/**
 * DateRangePicker Component
 * Date range selection for analytics
 */

import React from 'react';
import { Box, TextField, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onChange: (startDate: Date, endDate: Date) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ startDate, endDate, onChange }) => {
  const handlePresetChange = (preset: string) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    let start = new Date();

    switch (preset) {
      case 'lastWeek':
        start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        break;
      case 'lastMonth':
        start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        break;
      case 'last7Days':
        start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        break;
      case 'last30Days':
        start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        break;
      default:
        return;
    }

    onChange(start, today);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box display="flex" gap={2} alignItems="center">
        <TextField
          select
          label="Quick Select"
          value=""
          onChange={(e) => handlePresetChange(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
          }}
        >
          <MenuItem value="" disabled>
            Select Preset
          </MenuItem>
          <MenuItem value="lastWeek">Last Week</MenuItem>
          <MenuItem value="last7Days">Last 7 Days</MenuItem>
          <MenuItem value="last30Days">Last 30 Days</MenuItem>
          <MenuItem value="lastMonth">Last Month</MenuItem>
        </TextField>
        <DatePicker
          label="Start Date"
          value={startDate}
          onChange={(newValue: Date | null) => {
            if (newValue) {
              const newStart = new Date(newValue);
              newStart.setHours(0, 0, 0, 0);
              // Ensure start date is not after end date
              if (newStart <= endDate) {
                onChange(newStart, endDate);
              } else {
                // If start is after end, adjust end date
                const newEnd = new Date(newStart);
                newEnd.setHours(23, 59, 59, 999);
                onChange(newStart, newEnd);
              }
            }
          }}
          maxDate={endDate}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={(newValue: Date | null) => {
            if (newValue) {
              const newEnd = new Date(newValue);
              newEnd.setHours(23, 59, 59, 999);
              // Ensure end date is not before start date
              if (newEnd >= startDate) {
                onChange(startDate, newEnd);
              } else {
                // If end is before start, adjust start date
                const newStart = new Date(newEnd);
                newStart.setHours(0, 0, 0, 0);
                onChange(newStart, newEnd);
              }
            }
          }}
          minDate={startDate}
          slotProps={{ textField: { size: 'small' } }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
